import * as React from "react";
import styled, { css } from "styled-components";
import { FullScreen } from "react-full-screen";

import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import { useCurrentUser } from "src/core/users/hooks";
import {
  DashboardProvider,
  DashboardMode,
  useDashboardContext
} from "src/core/dashboard/contexts/DashboardContext";
import {
  DashboardSwitcher,
  DashboardGlobalFilters,
  DashboardModeSwitcher,
  CreateWidgetToolbar,
  DashboardGridLayout
} from "src/core/dashboard/components";
import { FixedBottomMenuWrapper } from "src/core/shared/components/menus/FixedBottomMenu";
import { DashboardFiltersRef } from "src/core/dashboard/components/DashboardGlobalFilters/DashboardGlobalFilters";

/**
 * Main Admin "Dashboard" View
 *
 * @returns
 */
const AdminDashboard: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const globalFiltersRef = React.useRef<DashboardFiltersRef>(null);

  const {
    mode, updateMode, fullScreenState
  } = useDashboardContext();

  const handleOnClose = React.useCallback(() => {
    updateMode(DashboardMode.View);
  }, [ updateMode ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <StyledDashboardWrapper $fullscreen={fullScreenState.active}>
          <DashboardSwitcher
            onDashboardSwitch={() => {
              globalFiltersRef.current?.setHasSetInitialDateFilters(false);
            }}
          />

          <DashboardGlobalFilters
            ref={globalFiltersRef}
            terminology={props.terminology}
          />

          <FullScreen
            handle={fullScreenState}
            className="$fullscreenStyle"
          >
            {mode === DashboardMode.View && (
              <div className="dashboard-mode-switcher">
                <DashboardModeSwitcher />
              </div>
            )}

            <DashboardGridLayout />

            <FixedBottomMenuWrapper
              $fullscreen={fullScreenState.active}
              active={mode === DashboardMode.Edit}
            >
              <CreateWidgetToolbar onClose={handleOnClose} />
            </FixedBottomMenuWrapper>
          </FullScreen>
        </StyledDashboardWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

/**
 * Admin dashboard wrapper to provide the main admin dashboard view with the dashboard context
 *
 * @returns
 */
const AdminDashboardWrapper: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const [ { currentRole } ] = useCurrentUser();

  React.useEffect(() => {
    if (currentRole?.code === "van-sales-admin") {
      history.push("/van-sales");
    }
  }, [ currentRole?.code, history ]);

  return (
    <DashboardProvider>
      <AdminDashboard {...props} />
    </DashboardProvider>
  );
};

const StyledDashboardWrapper = styled(Grid.Box)<{ $fullscreen: boolean; }>`
  width: 100%;
  position: relative;

  .$fullscreenStyle { 
    overflow: auto; 
    ${({ $fullscreen }) => $fullscreen ? css`width: 100vw;` : ""}
  }

  .dashboard-mode-switcher{
    display: flex;
    justify-content: flex-end;
  }
`;

export default AdminDashboardWrapper;
